import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../apiHelpers';
import dayjs from 'dayjs';
import Panel from '../../components/ui/Panel';
import { CATEGORY, alpha } from '../../components/ui/categoryColors';

const HEAT = [alpha(CATEGORY.blue.mark, 0.25), alpha(CATEGORY.blue.mark, 0.5), alpha(CATEGORY.orange.mark, 0.6), alpha(CATEGORY.red.mark, 0.8)];

const EventCalendarHeatmapModule = () => {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await api.get('/events', { silent: true });
        setEvents((data || []).filter(e => e.status !== 'cancelled'));
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return null;

  // Build 8-week calendar starting from this week's Monday
  const today = dayjs();
  const startOfWeek = today.startOf('week');
  const weeks = 8;
  const days = [];
  for (let i = 0; i < weeks * 7; i++) {
    days.push(startOfWeek.add(i, 'day'));
  }

  // Count drones committed per day
  const dronesByDay = {};
  events.forEach(e => {
    const start = dayjs(e.event_date).subtract(7, 'day'); // prep window
    const end = dayjs(e.end_date || e.event_date).add(3, 'day'); // post buffer
    const totalDrones = (parseInt(e.ls_drone_count) || 0) + (parseInt(e.pyro_drone_count) || 0);
    let d = start;
    while (d.isBefore(end) || d.isSame(end, 'day')) {
      const key = d.format('YYYY-MM-DD');
      dronesByDay[key] = (dronesByDay[key] || 0) + totalDrones;
      d = d.add(1, 'day');
    }
  });

  const maxDrones = Math.max(1, ...Object.values(dronesByDay));
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getIntensity = (count) => {
    if (!count) return { className: 'bg-surface-container-high' };
    const ratio = count / maxDrones;
    if (ratio > 0.75) return { style: { background: HEAT[3] } };
    if (ratio > 0.5) return { style: { background: HEAT[2] } };
    if (ratio > 0.25) return { style: { background: HEAT[1] } };
    return { style: { background: HEAT[0] } };
  };

  return (
    <Panel
      title="Event Heat Map"
      titleStyle="section"
      accent="blue"
      lit
      level={3}
      actions={(
        <Link to="/events/calendar" className="text-xs text-on-surface-variant hover:text-primary transition-colors">
          Full calendar
        </Link>
      )}
    >
      {/* Day labels */}
      <div className="flex gap-[3px] mb-1">
        <div className="w-5" />
        {dayNames.map((name, i) => (
          <div key={i} className="flex-1 text-center text-[9px] text-on-surface-variant">{name}</div>
        ))}
      </div>

      {/* Grid */}
      <div className="space-y-[3px]">
        {Array.from({ length: weeks }).map((_, weekIdx) => {
          const weekStart = startOfWeek.add(weekIdx * 7, 'day');
          return (
            <div key={weekIdx} className="flex gap-[3px] items-center">
              <div className="w-5 text-[9px] text-on-surface-variant text-right pr-1">
                {weekStart.format('M/D')}
              </div>
              {Array.from({ length: 7 }).map((_, dayIdx) => {
                const day = weekStart.add(dayIdx, 'day');
                const key = day.format('YYYY-MM-DD');
                const count = dronesByDay[key] || 0;
                const isToday = day.isSame(today, 'day');
                const heat = getIntensity(count);

                return (
                  <div
                    key={dayIdx}
                    className={`flex-1 aspect-square rounded-sm ${heat.className || ''}
                      ${isToday ? 'ring-1 ring-primary/60' : ''}
                      transition-colors duration-200`}
                    style={heat.style}
                    title={`${day.format('MMM D')}: ${count} drones committed`}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-1.5 mt-2">
        <span className="text-[9px] text-on-surface-variant">Less</span>
        <div className="w-3 h-3 rounded-sm bg-surface-container-high" />
        {HEAT.map((bg) => <div key={bg} className="w-3 h-3 rounded-sm" style={{ background: bg }} />)}
        <span className="text-[9px] text-on-surface-variant">More</span>
      </div>
    </Panel>
  );
};

export default EventCalendarHeatmapModule;
